import { Box } from '@chakra-ui/react'
import type { WizardTravelAdditionalData } from 'BrokerTsLib/src/dtos'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from '@/components/Loading'
import { LoadingFailedAlert } from '@/components/LoadingFailedAlert'
import { Note } from '@/components/Note'
import { Steps } from '@/components/Steps/Steps'
import { WizzardHeader } from '@/components/WizzardHeader/WizzardHeader'
import { Step3 } from '@/containers/steps/travel/Step3'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { QuotesProvider } from '@/context/QuotesContext'
import { SelectedQuoteProvider } from '@/context/SelectedQuoteContext'
import { SidebarProvider } from '@/context/SidebarContext'
import { StepsProvider } from '@/context/StepsContext'
import { WizzardHeaderProvider } from '@/context/WizzardHeaderContext'
import { WithAuth } from '@/hoc/WithAuth'
import { useGetAdditionalData } from '@/hooks/useWizard'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'

export const Step3Sections = {
  pojistnik: 1,
  persons: 2,
  vehicle: 3
} as const

export type Step3Sections = typeof Step3Sections[keyof typeof Step3Sections]

const Step3PageComponent = () => {
  const navigate = useNavigate()
  const [noteText, setNoteText] = useState<string | undefined>(undefined)

  const { insuranceParams, insuranceParamsId: paramsId, isLoadingData, isFetchingData, refetchInsuranceParams, error } = useInsuranceParams()
  const { isLoading: isLoadingFormData, data: formData, refetch: refetchFormData } = useGetAdditionalData(paramsId!, {
    enabled: false,
    cacheTime: 0,
  })

  const data = formData?.data as WizardTravelAdditionalData

  useEffect(() => {
    if (!insuranceParams) return

    if (!insuranceParams.generalParams.selectedOfferId && !isFetchingData && !isLoadingData) {
      navigate(`/travel/steps/2/${paramsId}`)
      return
    }

    refetchFormData().
      then((refetchResp) => {
        if (refetchResp.isSuccess && (refetchResp.data?.data as WizardTravelAdditionalData)?.vehicleData?.inputRequired) {
          setPageSections(origSections => {
            const newSections = []
            newSections.push(...origSections)
            if (origSections.length < 3) {
              newSections.push({ id: 'section_' + Step3Sections.vehicle, title: `${Step3Sections.vehicle}. Vozidlo` })
            }
            return newSections
          })
        }
      })
  }, [insuranceParams, isFetchingData])

  const [pageSections, setPageSections] = useState([
    { id: 'section_' + Step3Sections.pojistnik, title: `${Step3Sections.pojistnik}. Pojistník`, },
    { id: 'section_' + Step3Sections.persons, title: `${Step3Sections.persons}. Pojištěné osoby`, }
  ])

  const handleCreateNote = () => {
    setNoteText('Nová poznámka')
  }

  return (
    <StepsProvider currentStepId={3}>
      <SidebarProvider pageSections={pageSections}>
        <WizzardHeaderProvider>
          <QuotesProvider>
            {isLoadingData && <Loading large={true} />}
            {!isLoadingData && error && <LoadingFailedAlert />}
            {(insuranceParams) &&
                            <SelectedQuoteProvider>
                              <PageWrapper
                                steps={<Steps />}
                                header={<WizzardHeader onCreateNote={handleCreateNote} />}
                                sidebar={<Sidebar />}
                                contentProps={{
                                  sx: {
                                    pt: { base: 0, md: 0 },
                                  },
                                }}
                              >
                                {noteText && noteText !== '' && (
                                  <Note
                                    date="11.10.2021 14:35"
                                    text={noteText}
                                    onChange={(text) => setNoteText(text)}
                                  />
                                )}

                                <Box sx={{ pt: { base: 0, md: !noteText ? 8 : 0 } }}>
                                  {isLoadingFormData && <Loading large={true} />}
                                  {!isLoadingFormData && formData?.data &&
                                            <Step3 data={data} />
                                  }
                                </Box>
                              </PageWrapper>
                            </SelectedQuoteProvider>
            }
          </QuotesProvider>
        </WizzardHeaderProvider>
      </SidebarProvider>
    </StepsProvider>
  )
}

export const Step3Page = WithAuth(Step3PageComponent)